import axios from 'axios';

const API_BASE_URL = '/api';

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add token to requests
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Handle response errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    // A wrong password on the sign-in form is also a 401; the form shows that message itself.
    if (error.response && error.response.status === 401 && !String(error.config?.url || '').includes('/auth/login')) {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

export const authAPI = {
  login: (credentials) => api.post('/auth/login', credentials),
  logout: () => api.post('/auth/logout'),
  changePassword: (currentPassword, newPassword) => api.post('/auth/change-password', { currentPassword, newPassword }),
};

export const academicAPI = {
  getYears: () => api.get('/academic/years'),
  getActiveYear: () => api.get('/academic/years').then(res => res.data.find(y => y.isActive)),
  createYear: (data) => api.post('/academic/years', data),
  updateYear: (id, data) => api.put(`/academic/years/${id}`, data),
  deleteYear: (id) => api.delete(`/academic/years/${id}`),
  getClasses: () => api.get('/academic/classes'),
  getClassById: (id) => api.get(`/academic/classes/${id}`),
  createClass: (data) => api.post('/academic/classes', data),
  updateClass: (id, data) => api.put(`/academic/classes/${id}`, data),
  deleteClass: (id) => api.delete(`/academic/classes/${id}`),
  getSections: () => api.get('/academic/sections'),
  getSectionsByClass: (classId) => api.get(`/academic/sections/class/${classId}`),
  createSection: (data) => api.post('/academic/sections', data),
  updateSection: (id, data) => api.put(`/academic/sections/${id}`, data),
  deleteSection: (id) => api.delete(`/academic/sections/${id}`),
  getSubjects: () => api.get('/academic/subjects'),
  createSubject: (data) => api.post('/academic/subjects', data),
  updateSubject: (id, data) => api.put(`/academic/subjects/${id}`, data),
  deleteSubject: (id) => api.delete(`/academic/subjects/${id}`),
  getCastes: () => api.get('/academic/castes'),
  createCaste: (data) => api.post('/academic/castes', data),
  updateCaste: (id, data) => api.put(`/academic/castes/${id}`, data),
  deleteCaste: (id) => api.delete(`/academic/castes/${id}`),
};

export const dashboardAPI = {
  getStats: () => api.get('/dashboard/stats'),
};

export const schoolProfileAPI = {
  get: () => api.get('/school-profile'),
  create: (data) => api.post('/school-profile', data),
  update: (id, data) => api.put(`/school-profile/${id}`, data),
};

export const studentAPI = {
  getAll: () => api.get('/students'),
  getById: (id) => api.get(`/students/${id}`),
  create: (data) => api.post('/students', data),
  update: (id, data) => api.put(`/students/${id}`, data),
  delete: (id) => api.delete(`/students/${id}`),
  search: (query) => api.get(`/students/search?query=${query}`),
  getByClass: (classId) => api.get(`/students/class/${classId}`),
  getByClassSection: (classId, sectionId) => api.get(`/students/class/${classId}/section/${sectionId}`),
  getParent: (id) => api.get(`/students/${id}/parent`),
  createParent: (id, data) => api.post(`/students/${id}/parent`, data),
  saveLogin: (id, username, password) => api.post(`/students/${id}/login`, { username, password }),
};

export const teacherAPI = {
  getAll: () => api.get('/teachers'),
  getById: (id) => api.get(`/teachers/${id}`),
  create: (data) => api.post('/teachers', data),
  update: (id, data) => api.put(`/teachers/${id}`, data),
  delete: (id) => api.delete(`/teachers/${id}`),
  getAssignments: () => api.get('/teachers/assignments'),
  createAssignment: (data) => api.post('/teachers/assignments', data),
  deleteAssignment: (id) => api.delete(`/teachers/assignments/${id}`),
  // Class Teacher
  getClassTeacher: (classId) => api.get(`/teachers/class-teacher/${classId}`),
  setClassTeacher: (teacherId, classId) => api.put(`/teachers/${teacherId}/class-teacher/${classId}`),
  removeClassTeacher: (classId) => api.delete(`/teachers/class-teacher/${classId}`),
};

export const attendanceAPI = {
  mark: (data) => api.post('/attendance/mark', data),
  getByDate: (date) => api.get(`/attendance/date/${date}`),
  getByClassSectionDate: (classId, sectionId, date) =>
    api.get(`/attendance/class/${classId}/section/${sectionId}/date/${date}`),
  getByStudent: (id) => api.get(`/attendance/student/${id}`),
  getTodayStats: () => api.get('/attendance/statistics/today'),
  getLeaves: () => api.get('/attendance/leaves'),
  getLeavesManage: (status) => api.get('/attendance/leaves/manage', { params: status ? { status } : {} }),
  applyLeave: (data) => api.post('/attendance/leaves', data),
  approveLeave: (id) => api.put(`/attendance/leaves/${id}/approve`),
  rejectLeave: (id, reason) => api.put(`/attendance/leaves/${id}/reject`, { reason }),
};

export const homeworkAPI = {
  getAll: () => api.get('/homework'),
  create: (data) => api.post('/homework', data),
  update: (id, data) => api.put(`/homework/${id}`, data),
  delete: (id) => api.delete(`/homework/${id}`),
  getByClass: (classId) => api.get(`/homework/class/${classId}`),
};

export const noticeAPI = {
  getAll: () => api.get('/notices'),
  getPublished: () => api.get('/notices/published'),
  create: (data) => api.post('/notices', data),
  update: (id, data) => api.put(`/notices/${id}`, data),
  delete: (id) => api.delete(`/notices/${id}`),
};

// The instance default forces Content-Type: application/json; clearing it here lets the
// browser set multipart/form-data with the correct boundary itself for a FormData body.
const multipart = { headers: { 'Content-Type': undefined } };

export const galleryAPI = {
  getAll: () => api.get('/gallery'),
  getOne: (id) => api.get(`/gallery/${id}`),
  create: (formData) => api.post('/gallery', formData, multipart),
  addPhotos: (id, formData) => api.post(`/gallery/${id}/photos`, formData, multipart),
  setPublished: (id, value) => api.put(`/gallery/${id}/publish`, null, { params: { value } }),
  deletePhoto: (photoId) => api.delete(`/gallery/photos/${photoId}`),
  delete: (id) => api.delete(`/gallery/${id}`),
};

export const examinationAPI = {
  getAll: () => api.get('/examinations'),
  create: (data) => api.post('/examinations', data),
  update: (id, data) => api.put(`/examinations/${id}`, data),
  delete: (id) => api.delete(`/examinations/${id}`),
  getSchedules: (examId) => api.get(`/examinations/${examId}/schedules`),
  createSchedule: (data) => api.post('/examinations/schedules', data),
  updateSchedule: (id, data) => api.put(`/examinations/schedules/${id}`, data),
  deleteSchedule: (id) => api.delete(`/examinations/schedules/${id}`),
  getMarks: (examId) => api.get(`/examinations/${examId}/marks`),
  getMarksByStudentExam: (examId, studentId) => api.get(`/examinations/${examId}/student/${studentId}/marks`),
  createMarks: (data) => api.post('/examinations/marks', data),
  createMarksBulk: (data) => api.post('/examinations/marks/bulk', data),
  updateMarks: (id, data) => api.put(`/examinations/marks/${id}`, data),
  deleteMarks: (id) => api.delete(`/examinations/marks/${id}`),
};

export const feeAPI = {
  getStructures: () => api.get('/fees/structures'),
  getStructureById: (id) => api.get(`/fees/structures/${id}`),
  createStructure: (data) => api.post('/fees/structures', data),
  updateStructure: (id, data) => api.put(`/fees/structures/${id}`, data),
  deleteStructure: (id) => api.delete(`/fees/structures/${id}`),
  getAllPayments: () => api.get('/fees/payments'),
  getStudentPayments: (studentId) => api.get(`/fees/payments/student/${studentId}`),
  getStudentStatus: (studentId) => api.get(`/fees/status/student/${studentId}`),
  createPayment: (data) => api.post('/fees/payments', data),
  getDailyReport: (date) => api.get(`/fees/reports/daily?date=${date}`),
  getMonthlyReport: (startDate, endDate) =>
    api.get(`/fees/reports/monthly?startDate=${startDate}&endDate=${endDate}`),
};

export const smsAPI = {
  getStatus: () => api.get('/sms/status'),
  getBalance: () => api.get('/sms/balance'),
  getLogs: () => api.get('/sms/logs'),
  sendCustom: (data) => api.post('/sms/send', data),
  sendToClass: (data) => api.post('/sms/send-to-class', data),
  sendAbsentAlert: (data) => api.post('/sms/absent-alert', data),
  sendNoticeAlert: (id, data) => api.post(`/sms/notice/${id}`, data),
  sendFeeReminder: (data) => api.post('/sms/fee-reminder', data),
  previewAbsent: (params) => api.get('/sms/preview/absent', { params }),
  previewFeePending: (params) => api.get('/sms/preview/fee-pending', { params }),
};

export const notesAPI = {
  getMy: () => api.get('/teacher-notes/my'),
  getViews: (noteId) => api.get(`/teacher-notes/${noteId}/views`),
  delete: (id) => api.delete(`/teacher-notes/${id}`),
};

export const ratingAPI = {
  getConfig: () => api.get('/ratings/config'),
  updateConfig: (data) => api.put('/ratings/admin/config', data),
  getCriteria: () => api.get('/ratings/admin/criteria'),
  createCriterion: (data) => api.post('/ratings/admin/criteria', data),
  updateCriterion: (id, data) => api.put(`/ratings/admin/criteria/${id}`, data),
  deleteCriterion: (id) => api.delete(`/ratings/admin/criteria/${id}`),
  reorderCriteria: (ids) => api.put('/ratings/admin/criteria/order', { ids }),
  getSettings: () => api.get('/ratings/admin/settings'),
  toggleSetting: (sectionId, enabled) => api.post(`/ratings/admin/settings/${sectionId}`, { enabled }),
  bulkSettings: (data) => api.post('/ratings/admin/settings/bulk', data),
  getDashboard: (params) => api.get('/ratings/admin/dashboard', { params }),
  getTeacherDetail: (teacherId, params) => api.get(`/ratings/admin/teacher/${teacherId}`, { params }),
  getParticipation: (params) => api.get('/ratings/admin/participation', { params }),
  getParticipationStudents: (sectionId) => api.get(`/ratings/admin/participation/section/${sectionId}`),
};

export const accountingAPI = {
  // Expenses
  getExpenses: () => api.get('/accounting/expenses'),
  getExpenseById: (id) => api.get(`/accounting/expenses/${id}`),
  getExpensesByCategory: (category) => api.get(`/accounting/expenses/category/${category}`),
  createExpense: (data) => api.post('/accounting/expenses', data),
  updateExpense: (id, data) => api.put(`/accounting/expenses/${id}`, data),
  deleteExpense: (id) => api.delete(`/accounting/expenses/${id}`),

  // Salary / Payroll
  getSalaries: () => api.get('/accounting/salaries'),
  getSalaryById: (id) => api.get(`/accounting/salaries/${id}`),
  getSalariesByTeacher: (teacherId) => api.get(`/accounting/salaries/teacher/${teacherId}`),
  getSalariesByMonth: (month, year) => api.get(`/accounting/salaries/month?month=${month}&year=${year}`),
  createSalary: (data) => api.post('/accounting/salaries', data),
  updateSalary: (id, data) => api.put(`/accounting/salaries/${id}`, data),
  deleteSalary: (id) => api.delete(`/accounting/salaries/${id}`),

  // Reports / Summary
  getSummary: (startDate, endDate) => api.get(`/accounting/summary?startDate=${startDate}&endDate=${endDate}`),
  getDailyReport: (date) => api.get(`/accounting/reports/daily?date=${date}`),
  getMonthlyReport: (startDate, endDate) =>
    api.get(`/accounting/reports/monthly?startDate=${startDate}&endDate=${endDate}`),
  getYearlyReport: (year) => api.get(`/accounting/reports/yearly?year=${year}`),

  // Other income
  getIncomes: (params) => api.get('/accounting/incomes', { params }),
  createIncome: (data) => api.post('/accounting/incomes', data),
  updateIncome: (id, data) => api.put(`/accounting/incomes/${id}`, data),
  deleteIncome: (id) => api.delete(`/accounting/incomes/${id}`),

  // Categories the admin controls
  getCategories: (params) => api.get('/accounting/categories', { params }),
  createCategory: (data) => api.post('/accounting/categories', data),
  updateCategory: (id, data) => api.put(`/accounting/categories/${id}`, data),
  deleteCategory: (id) => api.delete(`/accounting/categories/${id}`),

  // Day book, trend, budgets, fee dues, payroll tools
  getLedger: (params) => api.get('/accounting/ledger', { params }),
  getTrend: (months) => api.get('/accounting/trend', { params: { months } }),
  getBudgets: (params) => api.get('/accounting/budgets', { params }),
  saveBudget: (data) => api.post('/accounting/budgets', data),
  deleteBudget: (id) => api.delete(`/accounting/budgets/${id}`),
  getFeeDues: (params) => api.get('/accounting/fee-dues', { params }),
  generatePayroll: (data) => api.post('/accounting/salaries/generate', data),
  markAllPaid: (data) => api.post('/accounting/salaries/mark-paid', data),

  // Vendors & vendor ledger
  getVendors: (includeInactive) => api.get('/accounting/vendors', { params: includeInactive ? { includeInactive: true } : {} }),
  getVendorLedger: (id) => api.get(`/accounting/vendors/${id}/ledger`),
  createVendor: (data) => api.post('/accounting/vendors', data),
  updateVendor: (id, data) => api.put(`/accounting/vendors/${id}`, data),
  deleteVendor: (id) => api.delete(`/accounting/vendors/${id}`),

  // Fixed assets
  getAssets: (category) => api.get('/accounting/assets', { params: category ? { category } : {} }),
  getAssetsSummary: () => api.get('/accounting/assets/summary'),
  createAsset: (data) => api.post('/accounting/assets', data),
  updateAsset: (id, data) => api.put(`/accounting/assets/${id}`, data),
  deleteAsset: (id) => api.delete(`/accounting/assets/${id}`),
};

export const libraryAPI = {
  getBooks: (params) => api.get('/library/books', { params }),
  createBook: (data) => api.post('/library/books', data),
  updateBook: (id, data) => api.put(`/library/books/${id}`, data),
  deleteBook: (id) => api.delete(`/library/books/${id}`),
  getIssues: (status) => api.get('/library/issues', { params: status ? { status } : {} }),
  issueBook: (data) => api.post('/library/issue', data),
  returnBook: (id, remarks) => api.put(`/library/issues/${id}/return`, remarks ? { remarks } : {}),
  markLost: (id) => api.put(`/library/issues/${id}/mark-lost`),
  markFinePaid: (id) => api.put(`/library/issues/${id}/mark-fine-paid`),
  getSummary: () => api.get('/library/summary'),
  getForStudent: (studentId) => api.get(`/library/student/${studentId}`),
};

export const clinicalAPI = {
  getPostings: (params) => api.get('/clinical/postings', { params }),
  createPosting: (data) => api.post('/clinical/postings', data),
  updatePosting: (id, data) => api.put(`/clinical/postings/${id}`, data),
  deletePosting: (id) => api.delete(`/clinical/postings/${id}`),
  getRoster: (id, date) => api.get(`/clinical/postings/${id}/roster`, { params: { date } }),
  markHours: (data) => api.post('/clinical/attendance', data),
};

export const hostelAPI = {
  getSummary: () => api.get('/hostel/summary'),
  getHostels: () => api.get('/hostel/hostels'),
  createHostel: (data) => api.post('/hostel/hostels', data),
  updateHostel: (id, data) => api.put(`/hostel/hostels/${id}`, data),
  deleteHostel: (id) => api.delete(`/hostel/hostels/${id}`),
  getRooms: (hostelId) => api.get('/hostel/rooms', { params: hostelId ? { hostelId } : {} }),
  createRoom: (data) => api.post('/hostel/rooms', data),
  updateRoom: (id, data) => api.put(`/hostel/rooms/${id}`, data),
  deleteRoom: (id) => api.delete(`/hostel/rooms/${id}`),
  getAllotments: (status) => api.get('/hostel/allotments', { params: status ? { status } : {} }),
  allot: (data) => api.post('/hostel/allotments', data),
  vacate: (id) => api.put(`/hostel/allotments/${id}/vacate`),

  // Outpasses (gate register)
  getOutpasses: (status) => api.get('/hostel/outpasses', { params: status ? { status } : {} }),
  createOutpass: (data) => api.post('/hostel/outpasses', data),
  markReturned: (id, remarks) => api.put(`/hostel/outpasses/${id}/return`, remarks ? { remarks } : {}),

  // Room change requests
  getRoomChangeRequests: (status) => api.get('/hostel/room-change-requests', { params: status ? { status } : {} }),
  approveRoomChange: (id) => api.put(`/hostel/room-change-requests/${id}/approve`),
  rejectRoomChange: (id, remarks) => api.put(`/hostel/room-change-requests/${id}/reject`, { remarks }),

  // Monthly hostel fee invoices
  getFeeInvoices: (params) => api.get('/hostel/fees/invoices', { params }),
  generateFeeInvoices: (data) => api.post('/hostel/fees/generate', data),
  markFeeInvoicePaid: (id, paymentMode) => api.put(`/hostel/fees/invoices/${id}/mark-paid`, { paymentMode }),
};

export const logbookAPI = {
  getSummary: () => api.get('/logbook/summary'),
  getPending: () => api.get('/logbook/pending'),
  getForStudent: (studentId) => api.get(`/logbook/student/${studentId}`),
  review: (id, data) => api.put(`/logbook/${id}/review`, data),
};

export const transportAPI = {
  getRoutes: () => api.get('/transport/routes'),
  getActiveRoutes: () => api.get('/transport/routes/active'),
  getRouteById: (id) => api.get(`/transport/routes/${id}`),
  createRoute: (data) => api.post('/transport/routes', data),
  updateRoute: (id, data) => api.put(`/transport/routes/${id}`, data),
  deleteRoute: (id) => api.delete(`/transport/routes/${id}`),
  getStudentsByRoute: (routeId) => api.get(`/transport/routes/${routeId}/students`),
  assignRoute: (studentId, routeId) => api.put(`/transport/students/${studentId}/route`, { routeId }),
  removeRoute: (studentId) => api.delete(`/transport/students/${studentId}/route`),
};

export const classReportAPI = {
  getSummary: () => api.get('/reports/class-students/summary'),
  getStudents: (params) => api.get('/reports/class-students', { params }),
};

export const timetableAPI = {
  getByBatch: (classId, sectionId) => api.get(`/timetable/class/${classId}/section/${sectionId}`),
  getForTeacher: (teacherId) => api.get(`/timetable/teacher/${teacherId}`),
  save: (data) => api.post('/timetable', data),
  remove: (id) => api.delete(`/timetable/${id}`),
  copy: (data) => api.post('/timetable/copy', data),
};

export const eventAPI = {
  getAll: () => api.get('/events'),
  create: (data) => api.post('/events', data),
  update: (id, data) => api.put(`/events/${id}`, data),
  remove: (id) => api.delete(`/events/${id}`),
};

export const admissionAPI = {
  getAll: (status) => api.get('/admission-inquiries', { params: status ? { status } : {} }),
  getSummary: () => api.get('/admission-inquiries/summary'),
  update: (id, data) => api.put(`/admission-inquiries/${id}`, data),
  delete: (id) => api.delete(`/admission-inquiries/${id}`),
};

export const publicAPI = {
  getCollegeProfile: () => api.get('/public/college-profile'),
  getGallery: () => api.get('/public/gallery'),
  getLeadership: () => api.get('/public/leadership'),
  submitInquiry: (data) => api.post('/public/admission-inquiries', data),
};

export default api;
